/**
 * Monatsprotokoll Auto-Versand Einstellungen
 * Admin-Tab für E-Mail-Konfiguration und Test-Versand
 */

import { useState, useEffect } from 'react';
import {
  Mail, Send, Clock, CheckCircle, AlertCircle,
  Loader2, CalendarDays, Power, PowerOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function MonatsprotokollSettings() {
  const [settings, setSettings] = useState({
    aktiviert: false,
    email_adresse: '',
    betreuer_name: 'System Administrator',
    letzter_versand: null,
    letzter_status: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await fetch(`${BACKEND_URL}/api/admin/monatsprotokoll/einstellungen`).then(r => r.json());
      setSettings(data);
    } catch {
      toast.error('Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleSave = async () => {
    if (settings.aktiviert && !settings.email_adresse) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }
    setSaving(true);
    try {
      const result = await fetch(`${BACKEND_URL}/api/admin/monatsprotokoll/einstellungen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aktiviert: settings.aktiviert,
          email_adresse: settings.email_adresse,
          betreuer_name: settings.betreuer_name
        })
      }).then(r => r.json());
      if (result.success) {
        toast.success('Einstellungen gespeichert');
      } else {
        toast.error(result.error || 'Fehler beim Speichern');
      }
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleTestVersand = async () => {
    if (!settings.email_adresse) {
      toast.error('Bitte zuerst eine E-Mail-Adresse eingeben und speichern');
      return;
    }
    setTesting(true);
    try {
      const result = await fetch(`${BACKEND_URL}/api/admin/monatsprotokoll/test-versand`, {
        method: 'POST'
      }).then(r => r.json());
      if (result.success) {
        toast.success(`Test-E-Mail an ${settings.email_adresse} gesendet`);
      } else {
        toast.error(result.error || 'Test-Versand fehlgeschlagen');
      }
      loadSettings();
    } catch {
      toast.error('Test-Versand fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="monatsprotokoll-settings">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-violet-500" />
            Automatischer Monatsprotokoll-Versand
          </CardTitle>
          <CardDescription>
            Am 1. jeden Monats um 08:00 Uhr wird das Monatsprotokoll des Vormonats automatisch als PDF per E-Mail versendet.
            Das Protokoll dient als Tätigkeitsnachweis gemäß §§ 1860, 1861 BGB.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Aktivierung */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
            <div className="flex items-center gap-3">
              {settings.aktiviert
                ? <Power className="w-5 h-5 text-emerald-500" />
                : <PowerOff className="w-5 h-5 text-slate-400" />
              }
              <div>
                <p className="font-medium text-slate-700">Auto-Versand</p>
                <p className="text-xs text-slate-500">
                  {settings.aktiviert ? 'Aktiv — Monatsprotokoll wird automatisch versendet' : 'Deaktiviert'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.aktiviert}
              onCheckedChange={(v) => setSettings(prev => ({ ...prev, aktiviert: v }))}
              data-testid="monatsprotokoll-toggle"
            />
          </div>

          {/* E-Mail-Adresse */}
          <div className="space-y-2">
            <Label htmlFor="mp-email" className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" /> Empfänger E-Mail-Adresse
            </Label>
            <Input
              id="mp-email"
              type="email"
              placeholder="betreuer@beispiel.de"
              value={settings.email_adresse}
              onChange={(e) => setSettings(prev => ({ ...prev, email_adresse: e.target.value }))}
              data-testid="monatsprotokoll-email"
            />
            <p className="text-xs text-slate-400">An diese Adresse wird das Monatsprotokoll gesendet</p>
          </div>

          {/* Betreuer-Name */}
          <div className="space-y-2">
            <Label htmlFor="mp-name">Betreuer-Name (für das PDF)</Label>
            <Input
              id="mp-name"
              placeholder="Max Mustermann"
              value={settings.betreuer_name}
              onChange={(e) => setSettings(prev => ({ ...prev, betreuer_name: e.target.value }))}
              data-testid="monatsprotokoll-name"
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="monatsprotokoll-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Einstellungen speichern
            </Button>
            <Button
              variant="outline"
              onClick={handleTestVersand}
              disabled={testing || !settings.email_adresse}
              data-testid="monatsprotokoll-test"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {testing ? 'Sende...' : 'Test-E-Mail senden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" /> Versand-Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {settings.letzter_versand ? (
            <div className="flex items-center gap-3">
              {settings.letzter_status?.startsWith('Erfolg')
                ? <CheckCircle className="w-5 h-5 text-emerald-500" />
                : <AlertCircle className="w-5 h-5 text-amber-500" />
              }
              <div>
                <p className="text-sm text-slate-700">
                  Letzter Versand: {new Date(settings.letzter_versand).toLocaleString('de-DE')}
                </p>
                <Badge
                  variant="outline"
                  className={`text-xs mt-1 ${
                    settings.letzter_status?.startsWith('Erfolg')
                      ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                      : 'border-amber-300 text-amber-700 bg-amber-50'
                  }`}
                >
                  {settings.letzter_status}
                </Badge>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Noch kein Versand durchgeführt</p>
          )}

          <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-xs text-blue-700">
              <strong>Zeitplan:</strong> Am 1. jeden Monats um 08:00 Uhr wird automatisch das Monatsprotokoll des
              Vormonats generiert und versendet. Voraussetzung: SMTP-Konfiguration unter "Kommunikation" aktiv.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
